import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Input, OnInit, signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TenantUserRoles } from '@buspro/shared/common';
import { NzDropDownDirective, NzDropdownMenuComponent } from 'ng-zorro-antd/dropdown';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NgxPermissionsModule } from 'ngx-permissions';
import { SidebarNavItem, SidebarNavItemComponent } from '../sidebar-nav-item/sidebar-nav-item.component';

export interface SidebarNavGroup {
  label: string;
  icon?: string;
  iconType?: 'fe' | 'fa';
  items: SidebarNavItem[];
  defaultExpanded?: boolean;
}

@Component({
  selector: 'tenant-sidebar-nav-group',
  standalone: true,
  imports: [
    NgClass,
    NgFor,
    NgIf,
    RouterLink,
    RouterLinkActive,
    SidebarNavItemComponent,
    NzDropDownDirective,
    NzDropdownMenuComponent,
    NgxPermissionsModule,
  ],
  template: `
    <div class="sidebar-nav-group" [class.collapsed]="isCollapsed">
      <button
        *ngIf="!isCollapsed"
        class="group-header"
        (click)="toggleExpanded()"
        [attr.aria-expanded]="isExpanded()"
        [attr.aria-controls]="'group-' + group.label"
      >
        <span class="group-icon" *ngIf="group.icon">
          <span
            [ngClass]="getGroupIconClasses()"
          ></span>
        </span>
        <span class="group-label">{{ group.label }}</span>
        <span class="group-chevron" [class.expanded]="isExpanded()">
          <span class="fe fe-chevron-down"></span>
        </span>
      </button>
      
      <!-- Collapsed state: show icon with flyout menu -->
      <div
        *ngIf="isCollapsed"
        class="group-header-collapsed flyout-trigger"
        nz-dropdown
        [nzDropdownMenu]="flyoutMenu"
        nzTrigger="hover"
        [nzPlacement]="flyoutPlacement"
        [nzOverlayClassName]="'sidebar-flyout-overlay'"
        [nzOverlayStyle]="flyoutOverlayStyle"
      >
        <span
          *ngIf="group.icon"
          [ngClass]="getGroupIconClasses()"
          class="group-icon-only"
        ></span>
      </div>

      <!-- Flyout menu for collapsed state -->
      <nz-dropdown-menu #flyoutMenu="nzDropdownMenu">
        <ul class="collapsed-group-menu">
          <li class="collapsed-group-title">{{ group.label }}</li>
          <li class="collapsed-menu-divider"></li>
          @for (item of group.items; track item.route) {
            @if (!item.roles || item.roles.length === 0) {
              <li
                [routerLink]="item.route"
                routerLinkActive="active"
                [routerLinkActiveOptions]="{ exact: item.exact ?? false }"
                class="collapsed-menu-item"
              >
                <span [ngClass]="getItemIconClasses(item)" class="item-icon"></span>
                <span class="item-label">{{ item.label }}</span>
                @if (item.badge !== undefined && item.badge > 0) {
                  <span class="item-badge">{{ item.badge }}</span>
                }
              </li>
            }
            @if (item.roles && item.roles.length > 0) {
              <li
                *ngxPermissionsOnly="item.roles"
                [routerLink]="item.route"
                routerLinkActive="active"
                [routerLinkActiveOptions]="{ exact: item.exact ?? false }"
                class="collapsed-menu-item"
              >
                <span [ngClass]="getItemIconClasses(item)" class="item-icon"></span>
                <span class="item-label">{{ item.label }}</span>
                @if (item.badge !== undefined && item.badge > 0) {
                  <span class="item-badge">{{ item.badge }}</span>
                }
              </li>
            }
          }
        </ul>
      </nz-dropdown-menu>
      
      <div
        class="group-items"
        [class.expanded]="isExpanded()"
        [class.collapsed]="isCollapsed"
        [id]="'group-' + group.label"
        [attr.aria-hidden]="!isExpanded()"
      >
        <tenant-sidebar-nav-item
          *ngFor="let item of group.items"
          [item]="item"
          [isCollapsed]="isCollapsed"
        ></tenant-sidebar-nav-item>
      </div>
    </div>
  `,
  styles: [
    `
      .sidebar-nav-group {
        margin-bottom: 0;
        padding-bottom: 8px;
        
        // Separator line between groups
        &:not(:last-child) {
          border-bottom: 1px solid var(--sidebar-group-separator);
          margin-bottom: 8px;
        }
        
        .group-header {
          display: flex;
          align-items: center;
          width: calc(100% - 24px);
          padding: 8px 10px;
          margin: 4px 12px 2px 12px;
          background: transparent;
          border: none;
          color: var(--sidebar-group-text);
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          cursor: pointer;
          border-radius: 4px;
          transition: all var(--transition-base);
          position: relative;
          
          // Left accent line on hover
          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 2px;
            height: 0;
            background-color: var(--sidebar-group-accent);
            border-radius: 1px;
            transition: height var(--transition-base);
          }
          
          &:hover {
            color: var(--sidebar-group-hover-text);
            background-color: transparent;
            
            &::before {
              height: 60%;
            }
            
            .group-icon {
              color: var(--sidebar-group-accent);
            }
          }
          
          .group-icon {
            font-size: 13px;
            width: 18px;
            height: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 8px;
            color: var(--sidebar-group-text);
            opacity: 0.8;
            transition: color var(--transition-base);
          }
          
          .group-label {
            flex: 1;
            text-align: left;
          }
          
          .group-chevron {
            transition: transform var(--transition-base);
            display: flex;
            align-items: center;
            color: var(--sidebar-group-text);
            font-size: 11px;
            opacity: 0.6;
            
            &.expanded {
              transform: rotate(180deg);
            }
          }
        }
        
        .group-header-collapsed {
          display: flex;
          justify-content: center;
          padding: 8px 4px;
          margin: 4px 4px;
          
          .group-icon-only {
            font-size: 18px;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--sidebar-icon);
            border-radius: 8px;
            transition: all var(--transition-base);
            cursor: pointer;
            
            &:hover {
              background-color: var(--sidebar-hover);
              // The sidebar's own text colour, so the collapsed rail brightens
              // the same way the expanded rows do rather than flicking to blue.
              color: var(--sidebar-text);
            }
          }
        }
        
        .group-items {
          max-height: 0;
          overflow: hidden;
          transition: max-height var(--transition-base);
          padding-left: 6px;
          margin-top: 2px;
          
          &.expanded {
            max-height: 2000px;
          }
        }
        
        // When sidebar is collapsed, hide nav items (shown in flyout instead)
        &.collapsed .group-items {
          display: none;
        }
      }

      .collapsed-group-menu {
        padding: 8px;
        min-width: 220px;
        max-width: 280px;
        // This flyout IS the sidebar's menu, and its text uses --sidebar-text
        // (always light, because the sidebar rail is always blue in both themes).
        // Use the SIDEBAR background/border — not the theme-following --header-bg,
        // which is white in light mode and produced invisible white-on-white text.
        background-color: var(--sidebar-bg);
        border-radius: 10px;
        box-shadow: var(--shadow-lg);
        border: 1px solid var(--sidebar-border);
        list-style: none;
        margin: 0;
        max-height: 70vh;
        overflow-y: auto;
        overflow-x: hidden;

        .collapsed-group-title {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--sidebar-text-secondary);
          padding: 6px 12px;
          margin-bottom: 4px;
        }

        .collapsed-menu-divider {
          height: 1px;
          background-color: var(--sidebar-border);
          margin: 4px 0 8px 0;
        }

        .collapsed-menu-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 12px;
          margin: 2px 0;
          border-radius: 6px;
          color: var(--sidebar-text);
          text-decoration: none;
          cursor: pointer;
          transition: all var(--transition-fast);
          font-size: 14px;

          &:hover {
            background-color: var(--sidebar-hover);
            color: var(--sidebar-text);

            .item-icon {
              // Matches sidebar-nav-item: follow the label instead of switching
              // to --accent-primary, which in light mode is #0072BC on a
              // --sidebar-hover of #00629E (1.28:1 — invisible). No scaling
              // either; it blurs a 16px icon font.
              color: inherit;
              opacity: 1;
            }
          }

          &.active {
            background-color: var(--sidebar-active);
            color: var(--sidebar-icon-active);
            font-weight: 500;
            border-left: 3px solid var(--sidebar-active-border);
            padding-left: 9px;

            .item-icon {
              color: var(--sidebar-icon-active);
            }
          }

          .item-icon {
            font-size: 16px;
            width: 18px;
            height: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--sidebar-icon);
            transition: all var(--transition-fast);
            flex-shrink: 0;
          }

          .item-label {
            flex: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .item-badge {
            background-color: var(--accent-primary);
            color: white;
            font-size: 10px;
            font-weight: 600;
            padding: 2px 6px;
            border-radius: 10px;
            min-width: 16px;
            text-align: center;
          }
        }
      }
    `,
  ],
})
export class SidebarNavGroupComponent implements OnInit {
  @Input() group!: SidebarNavGroup;
  @Input() isCollapsed = false;
  @Input() storageKey?: string;

  isExpanded = signal(false);
  private readonly STORAGE_PREFIX = 'sidebar-group-';

  ngOnInit() {
    // Load saved state or use default
    const savedState = this.loadExpandedState();
    // If no saved state and defaultExpanded is true, expand by default
    const initialState = savedState !== null ? savedState : (this.group.defaultExpanded ?? false);
    this.isExpanded.set(initialState);
  }

  toggleExpanded() {
    const newState = !this.isExpanded();
    this.isExpanded.set(newState);
    this.saveExpandedState(newState);
  }

  private loadExpandedState(): boolean | null {
    if (!this.storageKey || typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    try {
      const stored = localStorage.getItem(this.STORAGE_PREFIX + this.storageKey);
      if (stored === null) {
        return null;
      }
      return stored === 'true';
    } catch {
      return null;
    }
  }

  private saveExpandedState(state: boolean): void {
    if (!this.storageKey || typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      localStorage.setItem(this.STORAGE_PREFIX + this.storageKey, String(state));
    } catch {
      // Ignore storage errors
    }
  }

  getGroupIconClasses(): { [key: string]: boolean } {
    const classes: { [key: string]: boolean } = {};
    if (this.group.iconType !== 'fa') {
      classes['fe'] = true;
    } else {
      classes['fa'] = true;
    }
    if (this.group.icon) {
      classes[this.group.icon] = true;
    }
    return classes;
  }

  getItemIconClasses(item: SidebarNavItem): { [key: string]: boolean } {
    const classes: { [key: string]: boolean } = {};
    if (item.iconType !== 'fa') {
      classes['fe'] = true;
    } else {
      classes['fa'] = true;
    }
    classes[item.icon] = true;
    return classes;
  }

  /** Placement: bottom-left but adjusted with margins to appear right-aligned at icon level */
  readonly flyoutPlacement = 'bottomLeft' as const;

  /** Push overlay to the right and up to align with icon */
  readonly flyoutOverlayStyle: { [key: string]: string } = {
    'margin-left': '54px', // Shift right to clear the 64px sidebar
    'margin-top': '-45px', // Shift up significantly to align with icon top
  };
}
